import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, UploadCloud } from "lucide-react";
import { API, authHeaders, toastErr } from "@/components/admin/AdminPage";

const inputCls = "w-full rounded-xl bg-white border-2 border-ink px-4 py-2.5 text-sm font-medium focus:outline-none focus:shadow-[3px_3px_0_#FF3B7E] transition-shadow";

const SCALARS = [
  ["brand_name", "Brand name", "text"],
  ["hero_headline", "Hero headline", "text"],
  ["hero_subtitle", "Hero subtitle", "textarea"],
  ["price", "Price (₹)", "number"],
  ["original_price", "Original price (₹, shown struck-through)", "number"],
  ["offer_badge", "Offer badge text", "text"],
  ["rating_text", "Rating badge text", "text"],
  ["guarantee_text", "Guarantee badge text", "text"],
  ["finalcta_heading", "Final CTA heading", "text"],
  ["finalcta_subtitle", "Final CTA subtitle", "text"],
  ["hero_player_text", "Hero player caption (use {name} for the typed name)", "text"],
  ["marquee_items", "Marquee items (one per line)", "textarea"],
  ["footer_blurb", "Footer brand blurb", "textarea"],
  ["footer_email", "Footer contact email", "text"],
  ["footer_good_to_know", "Footer 'Good to know' items (one per line)", "textarea"],
  ["footer_credit_text", "Footer credit name (leave empty to hide)", "text"],
  ["footer_credit_url", "Footer credit link URL", "text"],
  ["social_instagram", "Instagram URL (leave empty to hide icon)", "text"],
  ["social_facebook", "Facebook URL (leave empty to hide icon)", "text"],
  ["social_x", "X (Twitter) URL (leave empty to hide icon)", "text"],
  ["social_youtube", "YouTube URL (leave empty to hide icon)", "text"],
  ["copyright_text", "Copyright line", "text"],
  ["nav_links_visible", "Show navigation menu links", "checkbox"],
];

const LISTS = [
  { key: "carousel", title: "Homepage Carousel Slides", empty: { title: "", category: "", poster: "", video_path: "", audio_path: "" }, fields: [
    { k: "title", label: "Slide title" },
    { k: "category", label: "Category badge" },
    { k: "poster", label: "Poster image URL (or upload video)" },
    { k: "video_path", label: "Video file", media: "video/*" },
    { k: "audio_path", label: "Playable song (audio)", media: "audio/*" },
  ]},
  { key: "occasions", title: "Occasion Cards", empty: { title: "", occasion: "", tagline: "", badge: "", img: "" }, fields: [
    { k: "title", label: "Card title" },
    { k: "occasion", label: "Occasion value (pre-fills the form)" },
    { k: "tagline", label: "Tagline" },
    { k: "badge", label: "Badge label" },
    { k: "img", label: "Image URL" },
  ]},
  { key: "steps", title: "How It Works Steps", empty: { title: "", body: "" }, fields: [
    { k: "title", label: "Step title" },
    { k: "body", label: "Step text", textarea: true },
  ]},
  { key: "genres", title: "Sample Genres", empty: { name: "", snippet: "" }, fields: [
    { k: "name", label: "Genre name" },
    { k: "snippet", label: "Lyric snippet", textarea: true },
  ]},
  { key: "comparison_rows", title: "Comparison Rows", empty: { feature: "", generic: false, ours: true }, fields: [
    { k: "feature", label: "Feature" },
    { k: "generic", label: "Flowers & cake", tri: true },
    { k: "ours", label: "Your brand", tri: true },
  ]},
  { key: "testimonials", title: "Testimonials", empty: { name: "", text: "" }, fields: [
    { k: "name", label: "Reviewer name" },
    { k: "text", label: "Review text", textarea: true },
  ]},
  { key: "legal_pages", title: "Legal Pages (footer Quick Links)", empty: { slug: "", title: "", body: "" }, fields: [
    { k: "title", label: "Page title" },
    { k: "slug", label: "URL slug (e.g. privacy-policy)" },
    { k: "body", label: "Page content", textarea: true },
  ]},
  { key: "faqs", title: "FAQs", empty: { q: "", a: "" }, fields: [
    { k: "q", label: "Question" },
    { k: "a", label: "Answer", textarea: true },
  ]},
];

function MediaInput({ value, onChange, accept, token }) {
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await axios.post(`${API}/admin/upload`, fd, authHeaders(token));
      onChange(data.path);
      toast.success("Uploaded");
    } catch (e) { toastErr(e, "Upload failed"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2">
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Upload or paste path" className={inputCls} />
      <label className="btn-pop shrink-0 inline-flex items-center gap-1.5 rounded-full bg-mint border-2 border-ink px-3.5 py-2 text-xs font-head font-bold cursor-pointer shadow-[2px_2px_0_#2D1A32]">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />} Upload
        <input type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files[0])} />
      </label>
    </div>
  );
}

const triVal = (v) => (v === true ? "yes" : v === false ? "no" : "partial");
const triSet = (s) => (s === "yes" ? true : s === "no" ? false : "Sort of");

export default function AdminContent({ token }) {
  const [content, setContent] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => setContent(r.data)).catch((e) => toastErr(e, "Could not load content"));
  }, []);

  if (!content) return <p className="text-plum font-medium text-sm">Loading…</p>;

  const patchList = (key, i, patch) =>
    setContent((c) => ({ ...c, [key]: c[key].map((item, j) => (j === i ? { ...item, ...patch } : item)) }));
  const addItem = (key, empty) => setContent((c) => ({ ...c, [key]: [...(c[key] || []), { ...empty }] }));
  const removeItem = (key, i) => setContent((c) => ({ ...c, [key]: c[key].filter((_, j) => j !== i) }));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await axios.put(`${API}/admin/content`,
        { ...content, price: Number(content.price), original_price: Number(content.original_price) },
        authHeaders(token));
      setContent(data);
      toast.success("Saved — live on the store now");
    } catch (e) { toastErr(e); }
    setBusy(false);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="admin-content-panel">
      <div className="rounded-2xl bg-white border-[3px] border-ink shadow-[6px_6px_0_#2D1A32] p-6 sm:p-8">
        <h3 className="font-display font-bold text-lg mb-5">Basics</h3>
        <div className="space-y-4">
          {SCALARS.map(([key, label, type]) => (
            <div key={key}>
              <label className="block font-head font-bold text-xs text-plum mb-1.5">{label}</label>
              {type === "checkbox" ? (
              <label className="flex items-center gap-2.5 cursor-pointer py-1">
                <input data-testid={`content-field-${key}`} type="checkbox" checked={content[key] !== false} onChange={(e) => setContent((c) => ({ ...c, [key]: e.target.checked }))} className="w-5 h-5 accent-pink" />
                <span className="text-sm font-medium text-plum">Enabled</span>
              </label>
            ) : type === "textarea" ? (
                <textarea data-testid={`content-field-${key}`} value={content[key] ?? ""} onChange={(e) => setContent((c) => ({ ...c, [key]: e.target.value }))} rows={key === "marquee_items" ? 5 : 2} className={`${inputCls} resize-none`} />
              ) : (
                <input data-testid={`content-field-${key}`} type={type} value={content[key] ?? ""} onChange={(e) => setContent((c) => ({ ...c, [key]: e.target.value }))} className={inputCls} />
              )}
            </div>
          ))}
          <div>
            <label className="block font-head font-bold text-xs text-plum mb-1.5">Hero player song (audio file)</label>
            <MediaInput value={content.hero_audio_path} onChange={(v) => setContent((c) => ({ ...c, hero_audio_path: v }))} accept="audio/*" token={token} />
          </div>
        </div>
      </div>

      {LISTS.map((list) => (
        <div key={list.key} className="rounded-2xl bg-white border-[3px] border-ink shadow-[6px_6px_0_#2D1A32] p-6 sm:p-8" data-testid={`content-list-${list.key}`}>
          <div className="flex items-center justify-between mb-5">
            <h3 className="font-display font-bold text-lg">{list.title}</h3>
            <button data-testid={`content-add-${list.key}`} onClick={() => addItem(list.key, list.empty)}
              className="btn-pop inline-flex items-center gap-1.5 rounded-full bg-sunny border-2 border-ink px-4 py-1.5 text-xs font-head font-bold shadow-[2px_2px_0_#2D1A32]">
              <Plus size={13} /> Add
            </button>
          </div>
          <div className="space-y-4">
            {(content[list.key] || []).map((item, i) => (
              <div key={i} className="rounded-xl bg-cream border-2 border-ink/20 p-4 relative">
                <button data-testid={`content-remove-${list.key}-${i}`} onClick={() => removeItem(list.key, i)} aria-label="Remove item"
                  className="absolute top-3 right-3 w-7 h-7 rounded-full border-2 border-ink bg-white flex items-center justify-center text-pinkdark hover:bg-pink hover:text-white transition-colors">
                  <Trash2 size={12} />
                </button>
                <div className="grid sm:grid-cols-2 gap-3 pr-8">
                  {list.fields.map((f) => (
                    <div key={f.k} className={f.textarea || f.media ? "sm:col-span-2" : ""}>
                      <label className="block font-head font-bold text-[11px] text-plum mb-1">{f.label}</label>
                      {f.media ? (
                        <MediaInput value={item[f.k]} onChange={(v) => patchList(list.key, i, { [f.k]: v })} accept={f.media} token={token} />
                      ) : f.textarea ? (
                        <textarea value={item[f.k] ?? ""} onChange={(e) => patchList(list.key, i, { [f.k]: e.target.value })} rows={2} className={`${inputCls} resize-none`} />
                      ) : f.tri ? (
                        <select value={triVal(item[f.k])} onChange={(e) => patchList(list.key, i, { [f.k]: triSet(e.target.value) })} className={inputCls}>
                          <option value="yes">Yes ✓</option>
                          <option value="no">No ✗</option>
                          <option value="partial">Partially</option>
                        </select>
                      ) : (
                        <input value={item[f.k] ?? ""} onChange={(e) => patchList(list.key, i, { [f.k]: e.target.value })} className={inputCls} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {!(content[list.key] || []).length && <p className="text-xs text-lilac font-medium">No items yet — click Add.</p>}
          </div>
        </div>
      ))}

      <button data-testid="content-save-button" onClick={save} disabled={busy}
        className="btn-pop sticky bottom-6 rounded-full bg-pink text-white font-head font-bold px-10 py-3.5 text-sm border-2 border-ink shadow-[5px_5px_0_#2D1A32] disabled:opacity-50 flex items-center gap-2">
        {busy && <Loader2 size={15} className="animate-spin" />} Save All Changes
      </button>
    </div>
  );
}
